import TicketTypesContainer from "@app-contexts/public/features/tickets/components/TicketTypesContainer/TicketTypesContainer.web";
import { PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { PURCHASE_SOURCE_PLATFORM_MAP } from "@shared/constants/enums";
import { cleanTicketType } from "@shared/modules/events/utils/ticket-type-utils";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Divider from "@ui-kit/data-display/Divider/Divider.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useMemo } from "react";
import {
  PatronItemSelectionItemProps,
  PatronItemSelectionProps,
} from "./PatronItemSelection.props";

const DEFAULT_PURCHASE_PLATFORM = PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id;
const DEFAULT_PAYMENT_TYPE = PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id;

/**
 * PatronItemSelectionItem is an individual item in the PatronItemSelection component.
 */
function PatronItemSelectionItem({
  item,
  event,
  startingFromPrice,
  holdDetailsData,
  basketVoucherCode,
  initialAmountRequested,
  onAmountRequested,
  ticketTypesPatronTypeCount,
  ticketsAuthorizedByPassword,
  ticketAuthArray,
  updateTicketAuthArray,
}: PatronItemSelectionItemProps) {
  return (
    <Box>
      <TicketTypesContainer
        tt={cleanTicketType(item)}
        eventData={event}
        venue={event.venue}
        isRecurring={Boolean(event.is_recurring)}
        isLoading={false}
        ticketsAuthorizedByPassword={ticketsAuthorizedByPassword}
        ticketAuthArray={ticketAuthArray}
        updateTicketAuthArray={updateTicketAuthArray}
        basketVoucherCode={basketVoucherCode as string | null}
        userVouchers={[]}
        dynamicHoldSlug={holdDetailsData?.on_hold_detail?.tracking_link_slug}
        purchaseLimits={{
          min: holdDetailsData?.on_hold_detail?.purchase_limit_min ?? 0,
          max: holdDetailsData?.on_hold_detail?.purchase_limit_max ?? 0,
        }}
        isPatronItemSelection
        startingFromPrice={startingFromPrice}
        ticketTypesPatronTypeCount={ticketTypesPatronTypeCount}
        initialAmountRequested={initialAmountRequested}
        onAmountRequested={onAmountRequested}
      />
    </Box>
  );
}

/**
 * PatronItemSelection is similar to TicketTypesContainer in that it is a container for ItemQuantitySelectInfo to select a quantity for a certain TicketType.
 * The difference is that this component allows for a "starting price" to be displayed alongside a list of items to select from.
 *
 * TicketTypesContainer
 * - TicketTypes only
 * - No starting price
 * - Single item
 *
 * PatronItemSelection
 * - Also TicketTypes only, though expansion to other items should be easy once TicketTypesContainer is refactored
 * - Starting price
 * - Multiple items
 */
function PatronItemSelection({
  title,
  items,
  event,
  startingFromPrice,
  basketVoucherCode,
  holdDetailsData,
  initialSelectedItems,
  onAmountRequested,
  ticketTypesPatronTypeCount,
  ticketsAuthorizedByPassword,
  ticketAuthArray,
  updateTicketAuthArray,
}: PatronItemSelectionProps) {
  const { t } = useTranslation("common");

  const startingPrice = useMemo(() => {
    const prices = items.map(
      (item) =>
        item.fees_pricing_info?.[DEFAULT_PURCHASE_PLATFORM]?.[
          DEFAULT_PAYMENT_TYPE
        ]?.total_price_no_tax
    );
    const minPriceNumber = Math.min(
      ...prices.map((price) => parseFloat(price as string))
    );

    // return string as it was from the prices array. Slower, but avoids floating point errors.
    return prices.find(
      (price) => parseFloat(price as string) === minPriceNumber
    );
  }, [items]);

  return (
    <Flex padding="16px 20px" flexDirection="column" gap="xs">
      {ticketTypesPatronTypeCount && ticketTypesPatronTypeCount > 1 && (
        <Flex width="100%" justifyContent="space-between" alignItems="center">
          <Box>
            <Text fontSize="lg" fontWeight={700}>
              {title}
            </Text>
          </Box>
          <Flex flexDirection="column" alignItems="flex-end">
            <Text fontSize="sm" color="gray.600">
              {t("Prices from")}
            </Text>
            <Text fontWeight={700}>
              <CurrencyFormat
                value={startingPrice}
                currency={event.venue?.currency}
                includeSuffix
                displayType="text"
              />
            </Text>
          </Flex>
        </Flex>
      )}
      <Flex flexDirection="column" gap="md">
        {items.map((item, index) => {
          return (
            <>
              <PatronItemSelectionItem
                key={item.id}
                item={item}
                event={event}
                startingFromPrice={startingFromPrice}
                basketVoucherCode={basketVoucherCode}
                holdDetailsData={holdDetailsData}
                initialAmountRequested={initialSelectedItems?.[item.id]}
                onAmountRequested={onAmountRequested}
                ticketTypesPatronTypeCount={ticketTypesPatronTypeCount}
                ticketsAuthorizedByPassword={ticketsAuthorizedByPassword}
                ticketAuthArray={ticketAuthArray}
                updateTicketAuthArray={updateTicketAuthArray}
              />
              {index < items.length - 1 && <Divider />}
            </>
          );
        })}
      </Flex>
    </Flex>
  );
}

export default PatronItemSelection;
